// SendManager — управление отправкой сообщений
// Доступен через window.sendManager

import morphdom from 'morphdom';
import {
	initDatepickers,
	initFormMulti,
	popupManager
} from '../../_Common/app.js';

export class SendManager {
	constructor() {
		this._bindGlobalEvents();
	}

	// ─── Открыть попап отправки ────────────────────────────────────────

	// Хелперы для вызова из onclick без JS-объектов (XSL не поддерживает {} в AVT)
	openSendInvoice(sendType, invoiceId) {
		this.openSend(sendType, { invoiceId });
	}

	openSendService(sendType, table, serviceId, invoiceId) {
		this.openSend(sendType, { table, serviceId, invoiceId });
	}

	openSendCosting(sendType, table, serviceId, costingId, invoiceId) {
		this.openSend(sendType, { table, serviceId, costingId, invoiceId });
	}

	async openSend(sendType, params = {}) {
		try {
			const data = {
				action: 'loadSendPopup',
				sendType: sendType,
				...params
			};

			const response = await window.ajax.post('/ajax.php?mid=47', data, { dataType: 'html' });

			// Определяем имя попапа по каналу
			const popupName = this._getPopupName(response);

			this._updateOrInsertPopup(`.${popupName}`, response);

			const popup = document.querySelector(`.${popupName}`);
			if (popup) {
				this._bindPopupEvents(popup);
				requestAnimationFrame(() => {
					popupManager.open(popupName);
				});
			}
		} catch (error) {
			console.error('[SendManager] openSend error:', error);
		}
	}

	// ─── Отправить ─────────────────────────────────────────────────────

	async send(popupEl) {
		if (!popupEl) popupEl = document.querySelector('.popup-send-email.active, .popup-send-wa.active, .popup-send-app.active');
		if (!popupEl) return;

		const sendType = popupEl.dataset.sendType;
		if (!sendType) return;

		// Собираем получателей (отмеченных)
		const recipients = [];
		popupEl.querySelectorAll('.popup-send__recipient input[type="checkbox"]:checked').forEach(cb => {
			recipients.push(cb.value);
		});

		if (!recipients.length) {
			alert('Please select at least one recipient');
			return;
		}

		// Subject (только для email)
		const subjectInput = popupEl.querySelector('input[name="subject"]');
		const subject = subjectInput ? subjectInput.value : '';

		// Body — из contenteditable (email) или textarea (wa/app)
		const bodyEditor = popupEl.querySelector('.popup-send__editor[data-name="body"]');
		const bodyTextarea = popupEl.querySelector('textarea[name="body"]');
		const body = bodyEditor ? bodyEditor.innerHTML : (bodyTextarea ? bodyTextarea.value : '');

		// Собираем FormData (поддержка файлов)
		const formData = new FormData();
		formData.append('action', 'executeSend');
		formData.append('sendType', sendType);
		formData.append('subject', subject);
		formData.append('body', body);
		formData.append('table', popupEl.dataset.table || '');
		formData.append('serviceId', popupEl.dataset.serviceId || '');
		formData.append('costingId', popupEl.dataset.costingId || '');
		formData.append('invoiceId', popupEl.dataset.invoiceId || '');
		recipients.forEach(r => formData.append('recipients[]', r));

		// Файлы: серверные (по имени) + локальные (File-объекты)
		popupEl.querySelectorAll('.popup-send__attachment').forEach(att => {
			if (att._file) {
				formData.append('files[]', att._file);
			} else {
				const hidden = att.querySelector('input[name="attachments[]"]');
				if (hidden) formData.append('attachments[]', hidden.value);
			}
		});

		// Кнопка — disable на время отправки
		const sendBtn = popupEl.querySelector('.popup-send__btn-send');
		if (sendBtn) {
			sendBtn.disabled = true;
			sendBtn.textContent = 'Sending...';
		}

		try {
			const response = await window.ajax.post('/ajax.php?mid=47', formData);

			if (response.success) {
				popupManager.closeAll();
				// На localhost открываем preview
				if (response.preview_url) {
					window.open(response.preview_url, '_blank');
					this._showNotification('Email saved as preview (localhost)');
				} else {
					this._showNotification('Message sent successfully');
				}
			} else {
				alert(response.error || 'Failed to send');
			}
		} catch (error) {
			console.error('[SendManager] send error:', error);
			alert('Failed to send message');
		} finally {
			if (sendBtn) {
				sendBtn.disabled = false;
				sendBtn.innerHTML = '<span class="material-symbols-sharp">send</span> Send';
			}
		}
	}

	// ─── Открыть историю ───────────────────────────────────────────────

	async openHistory(table, relId, invoiceId) {
		try {
			const data = {
				action: 'loadSendHistory',
				table: table || '',
				relId: relId || '',
				invoiceId: invoiceId || '',
			};

			const response = await window.ajax.post('/ajax.php?mid=47', data, { dataType: 'html' });

			this._updateOrInsertPopup('.popup-send-history', response);

			const popup = document.querySelector('.popup-send-history');
			if (popup) {
				this._bindHistoryEvents(popup);
				requestAnimationFrame(() => {
					popupManager.open('popup-send-history');
				});
			}
		} catch (error) {
			console.error('[SendManager] openHistory error:', error);
		}
	}

	// ─── Копирование текста ───────────────────────────────────────────

	copyText(el) {
		if (!el) return;

		if (el.tagName === 'TEXTAREA' || el.tagName === 'INPUT') {
			// Для textarea — копируем plain text
			navigator.clipboard.writeText(el.value).then(() => {
				this._showNotification('Text copied to clipboard');
			});
		} else {
			// Для contenteditable — копируем с HTML-разметкой
			const range = document.createRange();
			range.selectNodeContents(el);
			const selection = window.getSelection();
			selection.removeAllRanges();
			selection.addRange(range);
			document.execCommand('copy');
			selection.removeAllRanges();
			this._showNotification('Text copied to clipboard');
		}
	}

	// ─── Управление вложениями ────────────────────────────────────────

	addAttachment(popupEl) {
		if (!popupEl) return;

		const input = document.createElement('input');
		input.type = 'file';
		input.multiple = true;
		input.style.display = 'none';

		input.addEventListener('change', () => {
			const list = popupEl.querySelector('.popup-send__attachments');
			if (!list) return;

			for (const file of input.files) {
				const row = document.createElement('div');
				row.className = 'popup-send__attachment';
				row.dataset.source = 'local';
				row.innerHTML = `
					<span class="material-symbols-sharp popup-send__attachment-icon">attach_file</span>
					<span class="popup-send__attachment-name">${file.name}</span>
					<button class="popup-send__attachment-remove" type="button"
						onclick="event.stopPropagation(); this.closest('.popup-send__attachment').remove()"
						title="Remove">
						<span class="material-symbols-sharp">close</span>
					</button>
				`;
				row._file = file;
				list.appendChild(row);
			}
			input.remove();
		});

		// Вставляем input внутрь попапа, чтобы клик не уходил за пределы .popup
		popupEl.appendChild(input);
		input.click();
	}

	// ─── Управление получателями ───────────────────────────────────────

	addRecipient(popupEl) {
		if (!popupEl) popupEl = document.querySelector('.popup-send-email.active, .popup-send-wa.active, .popup-send-app.active');
		if (!popupEl) return;

		const list = popupEl.querySelector('.popup-send__recipients');
		if (!list) return;

		const channel = popupEl.dataset.channel;
		const placeholder = channel === 'wa' ? 'Phone number' : (channel === 'app' ? 'Name' : 'Email address');

		const row = document.createElement('div');
		row.className = 'popup-send__recipient popup-send__recipient--new';
		row.innerHTML = `
			<input type="checkbox" checked value="" class="form-check__input">
			<input type="text" class="form__input form__input--32 popup-send__recipient-input"
				placeholder="${placeholder}"
				onchange="this.closest('.popup-send__recipient').querySelector('input[type=checkbox]').value = this.value">
			<button class="popup-send__recipient-remove" onclick="event.stopPropagation(); this.closest('.popup-send__recipient').remove()" title="Remove">
				<span class="material-symbols-sharp">close</span>
			</button>
		`;

		list.appendChild(row);

		// Фокус на новое поле
		const input = row.querySelector('.popup-send__recipient-input');
		if (input) input.focus();
	}

	// ─── Приватные методы ──────────────────────────────────────────────

	_getPopupName(html) {
		const match = html.match(/data-popup-name="([^"]+)"/);
		return match ? match[1] : 'popup-send-email';
	}

	_updateOrInsertPopup(selector, html) {
		const existing = document.querySelector(selector);
		if (existing) {
			// Удаляем старый и вставляем новый — morphdom может оставлять артефакты
			existing.remove();
		}
		const container = document.querySelector('.page') || document.body;
		container.insertAdjacentHTML('beforeend', html.trim());
	}

	_bindPopupEvents(popup) {
		popupManager.bindPopupEvents(popup);
		initDatepickers(popup);
		initFormMulti(popup);
	}

	_bindHistoryEvents(popup) {
		popupManager.bindPopupEvents(popup);

		// Тогглер для раскрытия полного текста
		popup.querySelectorAll('.popup-send-history__toggler').forEach(btn => {
			btn.addEventListener('click', () => {
				btn.closest('.popup-send-history__item').classList.toggle('expanded');
			});
		});
	}

	_bindGlobalEvents() {
		// Глобальные события делегируются через document
	}

	_showNotification(message) {
		// Простое уведомление
		const el = document.createElement('div');
		el.className = 'send-notification';
		el.textContent = message;
		document.body.appendChild(el);

		requestAnimationFrame(() => el.classList.add('active'));

		setTimeout(() => {
			el.classList.remove('active');
			setTimeout(() => el.remove(), 300);
		}, 3000);
	}
}
